import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useEffect, lazy, Suspense } from "react";
import { trackPageView } from "@/lib/analytics";
import { CookieBanner } from "@/components/CookieBanner";

// Lazy load route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const OpenDQDocs = lazy(() => import("./pages/OpenDQDocs"));
// OpenDQDashboard route disabled
const OpenDQProduct = lazy(() => import("./pages/OpenDQProduct"));
const CloudDQProduct = lazy(() => import("./pages/CloudDQProduct"));
const EnterpriseAIProduct = lazy(() => import("./pages/EnterpriseAIProduct"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const DummyCheckout = lazy(() => import("./pages/DummyCheckout"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Security = lazy(() => import("./pages/Security"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Videos = lazy(() => import("./pages/Videos"));
const VideoPost = lazy(() => import("./pages/VideoPost"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Route wrapper with error boundary that resets on navigation
 */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <ErrorBoundary key={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Setup global error handlers for unhandled promise rejections
 */
function GlobalErrorHandlers() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (import.meta.env.DEV) {
        console.error("Unhandled promise rejection:", event.reason);
      }
      // Prevent default browser behavior
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return null;
}

/**
 * Analytics page view tracker and scroll handler
 * Automatically tracks page views on route changes and handles scroll behavior
 */
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Handle hash-based scrolling or scroll to top
    if (location.hash) {
      // Small delay to ensure DOM is ready after navigation
      const timeoutId = setTimeout(() => {
        const elementId = location.hash.slice(1); // Remove the '#'
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      // Scroll to top on route change without hash
      window.scrollTo(0, 0);
    }

    // Track page view on route change
    trackPageView(location.pathname + location.search + location.hash, document.title);
  }, [location]);

  return null;
}

/**
 * Loading fallback component for lazy-loaded routes
 */
function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <GlobalErrorHandlers />
          <AnalyticsTracker />
          <CookieBanner />
          <RouteErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/opendq-docs" element={<OpenDQDocs />} />
                <Route path="/opendq" element={<OpenDQProduct />} />
                <Route path="/scient-cloud" element={<CloudDQProduct />} />
                <Route path="/enterprise-ai" element={<EnterpriseAIProduct />} />
                <Route path="/dummy-checkout" element={<DummyCheckout />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/videos/:slug" element={<VideoPost />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
